import {
	isBefore,
	localOrUtcTimestampToLocalDate,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { isZeroMoney } from "@sembark-travel/money"
import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Component,
	Divider,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Money,
	RelativeTime,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Table,
	TableFooterDataCell,
	Tabs,
	TabsList,
	Text,
	Time,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	Dialog,
	DialogBody,
	DialogFooter,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Link } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ICursorListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react"
import useSWR from "swr"
import { Required } from "utility-types"
import * as Validator from "yup"
import { ActivityLogs } from "../ActivityLogs"
import { IScheduledCab } from "../CabScheduling"
import { AddCommentInDialog, CommentableCommentsList } from "../Comments"
import { Email, IContact, IPhoneNumber, PhoneNumber } from "../Contacts"
import { ITripHotelBooking } from "../HotelBookings"
import {
	IsPGPresent,
	RegenratePGLinkWithDeleteDialog,
	TPGInvoice,
	usePaymentGatewayIntegratedAppsCount,
} from "../Integrations"
import { generatePath } from "../router-utils"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import { ITrip } from "../Trips"
import {
	GeneratedReceiptDownload,
	PaymentReceiptDialog,
} from "./GenerateReceiptAssets"
import { LogTransaction } from "./LogTransaction"
import { ApiRequestStatus, IInstalment } from "./store"
import { TTourist } from "../Tourists"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { IQuoteRequest } from "../QuoteRequests"
import { instalmentsXHR, useGetInstalmentState } from "./utils"
import {
	BulkPaymentInstalmentTable,
	SpinnerWithMessage,
	RefreshComponent,
} from "./List"
import { TVisaBooking } from "../VisaBookings"

function XHR(xhr: XHRInstance) {
	return {
		async show(instalmentId: number | string, params?: unknown) {
			return xhr
				.get<{ data: IInstalment }>(`/instalments/${instalmentId}`, { params })
				.then((resp) => resp.data)
		},
		async verifyPaidInstalment(
			data: Array<number>,
			batchId?: null | number
		): Promise<Array<IInstalment>> {
			return xhr
				.post("/verified-instalments", {
					items: data,
					verify_batch: batchId ? 1 : 0,
				})
				.then((resp) => resp.data.data)
		},
		async unverifyPaidInstalment(
			data: Array<number>,
			batchId?: null | number
		): Promise<Array<IInstalment>> {
			return xhr
				.delete("/verified-instalments", {
					data: { items: data, unverify_batch: batchId ? 1 : 0 },
				})
				.then((resp) => resp.data.data)
		},
		async deleteInstalment(instalmentId: number): Promise<void> {
			return xhr.delete(`/instalments/${instalmentId}`)
		},
		async revertInstalmentTransactions(
			instalmentId: number,
			data: unknown
		): Promise<void> {
			return xhr.post(`/revert-instalment-transactions/${instalmentId}`, data)
		},
		async revertBulkInstalmentTransactions(
			batchId: number,
			data: unknown
		): Promise<void> {
			return xhr.post(`/revert-bulk-payment-instalment-batch/${batchId}`, data)
		},
		async generatePGInvoice(
			instalmentId: number
		): Promise<{ data: IInstalment }> {
			return xhr
				.post(`/instalments/${instalmentId}/pg-invoices`)
				.then((resp) => resp.data)
		},
	}
}

export function InstalmentItemInDialog({
	children,
	...props
}: React.ComponentProps<typeof InstalmentItem> & {
	children: (props: { show: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ show: open })}
			<Dialog open={isOpen} onClose={close} title="Instalment Details">
				<Dialog.Body>
					<InstalmentItem {...props} />
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function InstalmentItem({
	onChange: propOnChange,
	instalmentId,
	readOnly,
	showSiblings,
	disableLogging,
	hideRevert = false,
}: Readonly<{
	instalmentId: number | string
	onChange?: () => void
	readOnly?: boolean
	showSiblings?: boolean
	disableLogging?: boolean
	hideRevert?: boolean
}>) {
	const xhr = useXHR()
	const { data, mutate } = useSWR(
		`/instalments/${instalmentId}`,
		() =>
			XHR(xhr).show(instalmentId, {
				include: ["batch_details", showSiblings ? "siblings" : ""]
					.filter(Boolean)
					.join(","),
			}),
		{ revalidateOnFocus: true, refreshInterval: 5000 }
	)

	const onChange = useCallback(() => {
		mutate()
		propOnChange?.()
	}, [mutate, propOnChange])
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	const instalment = data.data
	const { payment, currency, pg_invoice } = instalment
	const { amount: totalPaymentAmount } = payment
	const instalmentNumber = withOrdinalSuffix(
		(payment?.instalments?.findIndex(
			(installment) => installment?.id === Number(instalmentId)
		) || 0) + 1
	)

	return (
		<Stack gap="8">
			<Stack gap="4">
				<InstalmentAmountHeader
					instalment={instalment}
					instalmentNumber={instalmentNumber}
					currency={currency}
					totalPaymentAmount={totalPaymentAmount}
					pgInvoice={pg_invoice}
					readOnly={readOnly}
					onChange={onChange}
				/>
				<ContactDetails instalment={instalment} />
				<InstalmentPaymentFlow
					instalment={instalment}
					onChange={onChange}
					mutate={() => {
						mutate()
					}}
					readOnly={readOnly}
					disableLogging={disableLogging}
					hideRevert={hideRevert}
				/>
			</Stack>
			<InstalmentDetailsTabs
				instalment={instalment}
				showSiblings={showSiblings}
				mutate={() => {
					mutate()
				}}
			/>
		</Stack>
	)
}

interface InstalmentPaymentFlowProps {
	instalment: IInstalment
	onChange: () => void
	mutate: () => void
	readOnly?: boolean
	disableLogging?: boolean
	hideRevert?: boolean
}

function InstalmentPaymentFlow({
	instalment,
	mutate,
	onChange,
	readOnly,
	disableLogging,
	hideRevert = false,
}: Readonly<InstalmentPaymentFlowProps>) {
	const { pg_invoice } = instalment
	const hasUnverifiedTx =
		pg_invoice?.unverified_successful_transactions?.length ||
		pg_invoice?.unverified_successful_transactions_count
	const canLogPayment = instalment.can_log_payment && !disableLogging
	return (
		<Box>
			{hasUnverifiedTx ? (
				<PaymentInProcess instalment={instalment} />
			) : canLogPayment ? (
				<LogPaymentSection
					instalment={instalment}
					onChange={onChange}
					mutate={mutate}
				/>
			) : null}

			<PaymentPaidSection
				instalment={instalment}
				onChange={onChange}
				mutate={mutate}
				readOnly={readOnly}
				hideRevert={hideRevert}
			/>
		</Box>
	)
}

interface InstalmentAmountHeaderProps {
	instalment: IInstalment
	instalmentNumber: string
	currency: string
	totalPaymentAmount: number
	pgInvoice?: Pick<
		TPGInvoice,
		"status" | "original_amount" | "original_currency" | "amount"
	>
	readOnly: boolean | undefined
	onChange: () => void
}

function InstalmentAmountHeader({
	instalment,
	instalmentNumber,
	currency,
	totalPaymentAmount,
	pgInvoice,
	readOnly,
	onChange,
}: Readonly<InstalmentAmountHeaderProps>) {
	const xhr = useXHR()
	return (
		<Box gap="4">
			<Inline flex="1">
				<Box display="flex" gap="4" width="full" alignItems="center">
					<Inline justifyContent="between" alignItems="center" width="full">
						<Inline alignItems="center" gap="2" flexWrap="wrap">
							<Text as="h1" fontWeight="semibold" fontSize="lg">
								Instalment Amount ({currency})
							</Text>
							<Inline alignItems="center" gap="2" fontSize="sm" flexWrap="wrap">
								<Badge accent>
									<Text fontSize="sm">{instalmentNumber} Instalment</Text>
								</Badge>
								{pgInvoice?.status === "paid" ? (
									<Badge success>
										<Text>
											<Icons.LightningBolt size="4" /> PG
										</Text>
									</Badge>
								) : null}
							</Inline>
						</Inline>
						<Inline gap="4" alignItems="center" flexWrap="wrap">
							{!readOnly && instalment.can_delete ? (
								<Button
									status="warning"
									onClick={() => {
										if (
											window.confirm(
												"Are you sure you want to delete this instalment ?"
											)
										) {
											XHR(xhr).deleteInstalment(instalment.id).then(onChange)
										}
									}}
								>
									<Icons.Trash /> Delete
								</Button>
							) : null}
						</Inline>
					</Inline>
				</Box>
			</Inline>
			<Stack gap="1">
				<Inline gap="1">
					<Text fontSize="base" fontWeight="semibold">
						{currency}
					</Text>
					<Inline justifyContent="start" alignItems="center" gap="2">
						<Money
							amount={instalment.amount}
							currency={instalment.currency}
							fontSize="xl"
							fontWeight="semibold"
							color="default"
						/>
						<Box>
							<Money
								fontSize="md"
								fontWeight="normal"
								color="muted"
								amount={totalPaymentAmount}
								currency={instalment.currency}
							/>
						</Box>
					</Inline>
				</Inline>
				<TripInfo instalment={instalment} />
				{instalment?.batch?.count && instalment.batch.count > 1 ? (
					<Inline
						bgColor={"primary"}
						borderWidth={"1"}
						borderColor={"primary"}
						color="primary"
						rounded="md"
						padding={"1"}
					>
						<Box>
							<Icons.Info />
						</Box>
						<Text>{`This instalment was logged by combining ${instalment.batch.count} payments into one.`}</Text>
					</Inline>
				) : null}
			</Stack>
		</Box>
	)
}

function TripInfo({ instalment }: Readonly<{ instalment: IInstalment }>) {
	const type = instalment.payment.paymentable_type
	const { label, link } = extractTripMeta(type, instalment)
	switch (type) {
		case "trips":
			return <TripInfoLink text={label} link={link} />
		case "hotel_bookings":
			return <TripInfoLink text={label} link={link} />
		case "scheduled_cabs":
			return <TripInfoLink text={label} link={link} />
		case "travel_activity_bookings":
			return <TripInfoLink text={label} link={link} />
		default:
			return null
	}
}

function TripInfoLink({
	text,
	link,
}: Readonly<{ text: string; link: string }>) {
	return (
		<Inline>
			<Box>
				<Link to={link} color="accent" fontWeight="semibold">
					{text}
				</Link>
			</Box>
		</Inline>
	)
}

type TripMeta = {
	link: string
	label: string
}

function extractTripMeta(type: string, instalment: IInstalment): TripMeta {
	const { payment } = instalment
	switch (type) {
		case "trips": {
			const p = payment.paymentable
			return {
				link: `${generatePath("/trips/:tripId/accounting", { tripId: `${payment.paymentable_id}` })}#${instalment.id}instalment`,
				label: `For Trip ${payment.paymentable_id} • ${p?.destinations[0]?.name} • ${p?.nights}N, ${p?.days}D`,
			}
		}
		case "hotel_bookings": {
			const h = payment.paymentable
			return {
				link: `${generatePath("/trips/:tripId/services-bookings/:serviceType", {
					tripId: payment.paymentable?.trip_id,
					serviceType: "hotels",
				})}#${payment.paymentable_id}booking`,
				label: `For Trip ${h?.trip?.id} • ${h?.trip?.destinations[0]?.name} • ${h?.trip?.nights}N, ${h?.trip?.days}D`,
			}
		}

		case "scheduled_cabs": {
			const t = payment.paymentable.cab_schedule.trip
			return {
				link: `${generatePath("/trips/:tripId/services-bookings/:serviceType", {
					tripId: payment.paymentable?.cab_schedule?.trip_id,
					serviceType: "cabs",
				})}#${payment.paymentable_id}booking`,
				label: `For Trip ${t.id} • ${t?.destinations[0]?.name} • ${t?.nights}N, ${t?.days}D`,
			}
		}

		case "travel_activity_bookings": {
			const t = payment.paymentable.trip
			return {
				link: `${generatePath("/trips/:tripId/services-bookings/:serviceType", {
					tripId: payment.paymentable?.trip_id,
					serviceType: "operationals",
				})}#${payment.paymentable_id}booking`,
				label: `For Trip ${t.id} • ${t?.destinations[0]?.name} • ${t?.nights}N, ${t?.days}D`,
			}
		}
		case "quote_requests": {
			const q = payment.paymentable
			const tripId = q.is_incoming
				? q.created_trip_id?.toString()
				: q.trip_id.toString()
			return {
				link: `${generatePath("/trips/:tripId/accounting/payments", {
					tripId: tripId || "",
				})}#${payment.paymentable_id}booking`,
				label: `For Trip ${tripId} • B2B`,
			}
		}

		default:
			return {
				link: "",
				label: "",
			}
	}
}

function ContactDetails({ instalment }: Readonly<{ instalment: IInstalment }>) {
	const rawType = instalment.payment.paymentable_type
	const { type, contact, supplier, details } = extractContactDetails(
		rawType,
		instalment
	)
	return (
		<Box>
			<Box
				paddingX="4"
				paddingY="2"
				borderWidth="1"
				borderColor="default"
				rounded="md"
				bgColor="subtle"
			>
				<Box paddingBottom="2">
					<Inline
						alignItems="center"
						justifyContent="between"
						flexWrap="wrap"
						gap="2"
					>
						<Text as="h3" fontWeight="semibold" fontSize="md">
							Contact Details
						</Text>
						<Badge primary>
							<Box textTransform="capitalize">{type}</Box>
						</Badge>
					</Inline>
				</Box>
				<Box>
					<Heading as="h4" fontWeight="normal" fontSize="base">
						{joinAttributes(
							[contact?.name, contact?.name],
							[supplier.name, supplier.name]
						)}
					</Heading>
					<Inline
						color="muted"
						gap="1"
						alignItems="center"
						flexWrap="wrap"
						wordBreak="all"
					>
						{joinAttributes(
							[contact?.email, <Email value={contact?.email} />],
							[
								contact.phone_numbers.length > 0,
								<PhoneNumber value={contact?.phone_numbers} />,
							]
						)}
					</Inline>
					{type !== "Guest" && details ? (
						<ContactDetailSection
							icon={details.icon}
							content={details.content}
						/>
					) : null}
				</Box>
			</Box>
		</Box>
	)
}

function ContactDetailSection({
	icon,
	content,
}: Readonly<{ icon: ReactNode; content: ReactNode }>) {
	return (
		<Stack>
			<Divider sm />
			<Inline color="muted" alignItems="start" gap="1">
				<Text>{icon}</Text>
				<Box>{content}</Box>
			</Inline>
		</Stack>
	)
}

type ContactDetailMeta = {
	icon: ReactNode
	content: ReactNode
} | null

type ContactDetailsMeta = {
	type: string
	contact: {
		name: string
		email: string
		phone_numbers: IPhoneNumber<unknown>[]
	}
	supplier: {
		name: string
	}
	details: ContactDetailMeta
}

function extractContactDetails(
	type: string,
	instalment: IInstalment
): ContactDetailsMeta {
	const { payment } = instalment
	const { paymentable: pt } = payment
	switch (type) {
		case "trips": {
			const ptType = pt as ITrip
			const isAgent = ptType.trip_source.is_agent

			// if isAgent is true,
			// then trip_source_contact, then contact,
			// fallback to empty object for destructuring

			let contactObj: IContact | TTourist | undefined = undefined
			if (isAgent) {
				contactObj = ptType.trip_source_contact
			} else {
				contactObj = ptType.tourist
			}
			const name = contactObj?.name ?? ""
			const email = contactObj?.email ?? ""
			const phone_numbers = contactObj?.phone_numbers ?? []
			return {
				type: isAgent ? "B2B Agent" : "Guest",
				contact: {
					name,
					email,
					phone_numbers,
				},
				supplier: {
					name: isAgent ? ptType?.trip_source.name : "",
				},
				details: ptType.tourist
					? {
							icon: isAgent ? <Icons.User /> : <Icons.Airplane />,
							content: joinAttributes(
								ptType.tourist?.name ?? "",
								[
									ptType.tourist?.email,
									<Email value={ptType.tourist?.email ?? ""} />,
								],
								[
									ptType.tourist?.phone_numbers?.length > 0,
									<PhoneNumber value={ptType.tourist?.phone_numbers ?? []} />,
								]
							),
						}
					: null,
			}
		}
		case "hotel_bookings": {
			const ptType = pt as ITripHotelBooking["bookings"][number]
			// if isBookingMethodDirect is true,
			// then hotel_contact,if null then hotel -> contact,
			// then supplier_contact,if null then supplier -> contact,
			// fallback to empty object for destructuring
			const isBookingMethodDirect = ptType.booking_method === "Direct Hotel"

			let contactObj: IContact | undefined = undefined
			if (isBookingMethodDirect) {
				contactObj = ptType.hotel_contact ?? ptType.hotel?.contact
			} else {
				contactObj = ptType.supplier_contact ?? ptType.supplier?.contact
			}
			const name = contactObj?.name ?? ""
			const email = contactObj?.email ?? ""
			const phone_numbers = contactObj?.phone_numbers ?? []

			return {
				type: "Hotel",
				contact: {
					name,
					email,
					phone_numbers,
				},
				supplier: {
					name: ptType?.hotel?.name,
				},
				details: {
					icon: <Icons.Bed />,
					content: joinAttributes(
						ptType.hotel.address.location.full_name ?? "",
						ptType.hotel.no_of_stars
							? `${ptType.hotel.no_of_stars} Star`
							: (ptType.hotel.stars ?? ""),
						ptType.booking_method ?? ""
					),
				},
			}
		}
		case "visa_bookings": {
			const ptType = pt as TVisaBooking
			// Prefer supplier_contact, then supplier.contact, fallback to empty object for destructuring
			const {
				name = "",
				email = "",
				phone_numbers = [],
			} = ptType.supplier_contact ?? ptType.supplier?.contact ?? {}

			return {
				type: "Visa",
				contact: {
					name,
					email,
					phone_numbers,
				},
				supplier: {
					name: ptType?.supplier?.name ?? "",
				},
				details: {
					icon: <Icons.Visa />,
					content: joinAttributes(
						ptType.visa_service.name ?? null,
						ptType.visa_type.name ?? null
					),
				},
			}
		}
		case "scheduled_cabs": {
			const ptType = pt as Required<
				IScheduledCab,
				"cab_schedule" | "transport_service_provider"
			>
			// Prefer transport_service_provider, fallback to empty object for destructuring
			const {
				name = "",
				email = "",
				phone_numbers = [],
			} = ptType.transport_service_provider.contact ?? {}
			return {
				type: "Transport",
				contact: {
					name,
					email,
					phone_numbers,
				},
				supplier: {
					name: ptType.transport_service_provider?.name,
				},
				details: {
					icon: <Icons.Bus />,
					content: ptType.cab_schedule?.transport_service?.name ?? "",
				},
			}
		}
		case "travel_activity_bookings": {
			const ptType = pt as Required<TTravelActiviytBooking>
			// Prefer supplier_contact, then supplier.contact, fallback to empty object for destructuring
			const {
				name = "",
				email = "",
				phone_numbers = [],
			} = ptType.supplier_contact ?? ptType.supplier?.contact ?? {}

			return {
				type: "Activities",
				contact: {
					name,
					email,
					phone_numbers,
				},
				supplier: {
					name: ptType?.supplier?.name ?? "",
				},
				details: {
					icon: <Icons.Ticket />,
					content: ptType.title ?? "",
				},
			}
		}
		case "quote_requests": {
			const quoteRequest = pt as Required<IQuoteRequest>
			const name = quoteRequest.is_incoming
				? quoteRequest.from.name
				: quoteRequest.to.name
			return {
				type: "B2B",
				contact: {
					name: name,
					email: "",
					phone_numbers: [],
				},
				supplier: {
					name: "",
				},
				details: null,
			}
		}

		default:
			return {
				type: "",
				contact: {
					name: "",
					email: "",
					phone_numbers: [],
				},
				supplier: {
					name: "",
				},
				details: {
					icon: <Icons.QuestionMarkCircle />,
					content: "No additional details available.",
				},
			}
	}
}

function InstalmentDetailsTabs({
	instalment,
	showSiblings,
	mutate,
}: Readonly<{
	instalment: IInstalment
	showSiblings: boolean | undefined
	mutate: () => void
}>) {
	const { payment } = instalment
	const { instalments } = payment
	return (
		<Tabs defaultActive="comments" layout="col" gap="4">
			<TabsList>
				<TabItem id="comments" color="subtle">
					Comments
				</TabItem>
				{showSiblings && instalments?.length > 1 ? (
					<TabItem id="relatedInstalments">Related Instalments</TabItem>
				) : null}
				<TabItem id="transactions">Transactions</TabItem>
				<TabItem id="activities">Activities</TabItem>
			</TabsList>
			<TabContent id="comments" unmountOnInactive key="comments">
				<CommentsList
					instalment={instalment}
					onChange={() => {
						mutate()
					}}
				/>
			</TabContent>
			{showSiblings && instalments?.length > 1 ? (
				<TabContent
					id="relatedInstalments"
					unmountOnInactive
					key="relatedInstalments"
				>
					<InstallmentList instalment={instalment} />
				</TabContent>
			) : null}
			<TabContent id="transactions" unmountOnInactive key="transactions">
				<TransactionsList instalment={instalment} />
			</TabContent>
			<TabContent id="activities" unmountOnInactive key="activities">
				<ActivityList id={instalment.id} />
			</TabContent>
		</Tabs>
	)
}

function InstallmentList({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	const { payment, currency } = instalment
	const { instalments } = payment
	return (
		<>
			<Stack gap="4">
				<Heading as="h4" fontSize="lg">
					Related Instalments
				</Heading>
				<Text color="muted" fontSize="base">
					All instalments for this payment
				</Text>
			</Stack>
			<Table
				headers={["# ID", "Due", <Box>Amount ({currency})</Box>, "Status"]}
				bordered
				hover
				responsive
				alignCols={{ 2: "right" }}
				rows={instalments.map(
					({
						id,
						due_at,
						due_at_local,
						currency,
						amount,
						paid_at,
						verified_at,
					}) => [
						<Box>
							<Inline gap="2">
								<Text>{id}</Text>
								{instalment.id === id ? <Badge primary>Current</Badge> : null}
							</Inline>
						</Box>,
						<Box>
							<Time timestamp={due_at} localTimestamp={due_at_local} />
						</Box>,
						<Money amount={amount} currency={currency} />,
						<Box>
							<Inline gap="2">
								{paid_at ? (
									<Time timestamp={paid_at}>
										<Badge success outlined>
											Paid
										</Badge>
									</Time>
								) : isBefore(
										localOrUtcTimestampToLocalDate(due_at_local, due_at)
								  ) ? (
									<Badge warning outlined>
										Overdue
									</Badge>
								) : (
									<Badge outlined>Due</Badge>
								)}
								{verified_at ? (
									<Time timestamp={verified_at}>
										<Badge success outlined>
											Verified
										</Badge>
									</Time>
								) : null}
							</Inline>
						</Box>,
					]
				)}
			>
				<tfoot>
					<tr>
						<TableFooterDataCell colSpan={2} textAlign="right">
							Total ({currency})
						</TableFooterDataCell>
						<TableFooterDataCell textAlign="right">
							<Money amount={payment.amount} currency={currency} />
						</TableFooterDataCell>
						<TableFooterDataCell>
							<Inline gap="2">
								<Badge success outlined title="Paid Amount">
									+
									<Money
										amount={instalments
											.filter((i) => i.paid_at)
											.reduce<number>(
												(paid, { amount }) => paid + Number(amount),
												0
											)}
										currency={currency}
									/>
								</Badge>
								<Badge warning outlined title="Due Amount">
									<Money
										amount={instalments
											.filter((i) => !i.paid_at)
											.reduce<number>(
												(paid, { amount }) => paid + Number(amount),
												0
											)}
										currency={currency}
									/>
								</Badge>
							</Inline>
						</TableFooterDataCell>
					</tr>
				</tfoot>
			</Table>
		</>
	)
}

function CommentsList({
	instalment,
	onChange,
}: Readonly<{
	instalment: IInstalment
	onChange: () => void
}>) {
	return (
		<Stack>
			<Inline gap="6" justifyContent="between" alignItems="center">
				<Heading as="h4" fontSize="lg">
					Comments
				</Heading>
				<Box>
					<AddCommentInDialog
						commentableId={instalment.id}
						commentableType="instalments"
						canMessageCustomer={
							instalment.payment?.paymentable_type === "trips"
						}
						onChange={() => onChange()}
						level="tertiary"
					>
						{({ onAdd }) => (
							<Button onClick={() => onAdd()} size="sm" level="tertiary">
								<Icons.Annotation />
							</Button>
						)}
					</AddCommentInDialog>
				</Box>
			</Inline>
			{instalment.comments?.length ? (
				<Stack
					style={{
						overflowY:
							(instalment.comments?.length ?? 0) > 2 ? "auto" : "visible",
						maxHeight: "240px",
					}}
					paddingX="2"
				>
					<CommentableCommentsList
						comments={instalment.comments}
						onChange={() => onChange()}
					/>
				</Stack>
			) : (
				<Inline justifyContent="center" alignItems="center">
					<Text fontSize="base" color="muted">
						No comments added!
					</Text>
				</Inline>
			)}
		</Stack>
	)
}

function TransactionsList({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	return (
		<Stack gap="2">
			<Heading as="h4" fontSize="lg">
				Transactions
			</Heading>
			{instalment.journals?.length ? (
				<Stack as="ol" listStyleType="disc" paddingLeft="4" gap="2">
					{instalment.journals.map((j) => (
						<Box as="li" key={j.id}>
							<Inline gap="1" flexWrap="wrap" wordBreak="words" fontSize="base">
								<Text>{j.description}</Text>
								<Text color="muted">
									<RelativeTime timestamp={j.created_at} />
								</Text>
							</Inline>
						</Box>
					))}
				</Stack>
			) : (
				<Inline justifyContent="center" alignItems="center">
					<Text fontSize="base" color="muted">
						No transactions available!
					</Text>
				</Inline>
			)}
		</Stack>
	)
}

function ActivityList({ id }: Readonly<{ id: number }>) {
	return (
		<Stack gap="2">
			<Heading as="h4" fontSize="lg">
				Activities
			</Heading>
			<Box>
				<ActivityLogs subjectId={id} subjectType="instalments" />
			</Box>
		</Stack>
	)
}

type VerifyPaymentFormProps = {
	instalment: IInstalment
	onCancel?: () => void
	onSubmit: (data: Array<number>, batchId?: number | null) => Promise<void>
}

type VerifyPaymentProps = Omit<VerifyPaymentFormProps, "onSubmit"> & {
	onSuccess?: () => void
	revert?: boolean
}

function VerifyPayment({
	onSuccess,
	instalment,
	revert,
	...props
}: VerifyPaymentProps) {
	const xhr = useXHR()
	const id = useId()
	const { data } = useSWR(`/instalments/${instalment.id}?${id}`, () =>
		XHR(xhr).show(instalment.id, {
			include: "batch_details",
		})
	)
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	if (!revert) {
		return (
			<VerifyPaymentForm
				instalment={data.data}
				onSubmit={async (instalments, batchId) => {
					await XHR(xhr).verifyPaidInstalment(instalments, batchId)
					showSnackbar("Verified successfully.")
					onSuccess?.()
				}}
				{...props}
			/>
		)
	}
	return (
		<UnverifyPaymentForm
			instalment={data.data}
			onSubmit={async (instalments, batchId) => {
				await XHR(xhr).unverifyPaidInstalment(instalments, batchId)
				showSnackbar("Unverified successfully.")
				onSuccess?.()
			}}
			{...props}
		/>
	)
}

export function VerifyPaymentInDialog({
	onSuccess,
	children,
	...props
}: Omit<VerifyPaymentProps, "onCancel"> & {
	children: (props: { verify: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ verify: open })}
			{isOpen ? (
				<Dialog
					open={isOpen}
					onClose={close}
					title={`${props.revert ? "Unverify" : "Verify"} Payment`}
				>
					<Dialog.Body>
						<VerifyPayment
							{...props}
							onCancel={() => close()}
							onSuccess={() => {
								onSuccess?.()
								close()
							}}
						/>
					</Dialog.Body>
				</Dialog>
			) : null}
		</>
	)
}

const verifyPaymentDataValidationSchema = Validator.object().shape({
	instalments: Validator.array().required(),
	confirmed: Validator.boolean()
		.equals([true], "Please confirm that this is a verified transaction.")
		.required("Please confirm that this is a verified transaction."),
})

function VerifyPaymentForm({
	instalment,
	onSubmit,
	onCancel,
}: Readonly<VerifyPaymentFormProps>) {
	const [initialValues] = useState(() => ({
		instalments: [instalment.id],
		confirmed: false,
		// set the confirmation for batch to true incase of only one instalment
		confirmed_batch_instalment_count:
			Number(instalment.batch?.count) > 1 ? false : true,
	}))
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			validationSchema={verifyPaymentDataValidationSchema}
			onSubmit={withServerErrors(async (values) => {
				if (
					!values.confirmed ||
					(!values.confirmed_batch_instalment_count &&
						Number(instalment.batch?.count) > 1)
				) {
					throw new Error(
						"Please select the checkbox to confirm that this is a verified transaction."
					)
				}
				await onSubmit(values.instalments, instalment.batch?.id)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="2">
						<Heading as="h4">Payment Details</Heading>
						<Text color="muted">
							Please review the instalment and transaction details.
						</Text>
						<Box
							paddingX="6"
							padding="4"
							borderWidth="1"
							rounded="md"
							bgColor="primary"
							borderColor="primary"
						>
							<InstalmentOverview instalment={instalment} />
						</Box>
					</Stack>
					{instalment.batch?.id && Number(instalment.batch?.count || 0) > 1 ? (
						<Stack gap={"2"} paddingY={"4"}>
							<Heading fontSize={"md"}>
								Trips and instalments involved in the payment
							</Heading>
							<BulkInstalmentList
								include={"batch_details"}
								batchId={instalment.batch.id}
							/>
						</Stack>
					) : null}
					<Divider sm />
					<Stack gap="4">
						<Stack gap="2">
							<Heading as="h4">Confirmation</Heading>
							<Text color="muted">
								Please provide your confirmation regarding this transaction.
							</Text>
						</Stack>
						<Box padding="6" boxShadow="inner" bgColor="subtle" rounded="lg">
							<SwitchInputField
								name="confirmed"
								label="I have verified with accounts that this is a valid transaction."
							/>
						</Box>
						<Alert status="warning">
							After verification, you can not update any details regarding this
							instalment and this transaction.
						</Alert>
						{instalment.batch &&
						Number(instalment.batch.count || 0) > 1 &&
						instalment.batch.paid_money_sum ? (
							<Box padding="4">
								<SwitchInputField
									name="confirmed_batch_instalment_count"
									label={
										<>
											This will verify all{" "}
											{pluralize("instalments", instalment.batch.count, true)}{" "}
											for a total amount of{" "}
											{instalment.batch?.paid_money_sum?.currency}{" "}
											<Money money={instalment.batch?.paid_money_sum} /> under
											this payment.
										</>
									}
								/>
							</Box>
						) : null}
					</Stack>
					<Divider />
					<Stack gap="4">
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								<Icons.BadgeCheckSolid /> I understand, this is verified
							</Button>
							{onCancel ? (
								<Button
									disabled={submitting}
									onClick={() => onCancel()}
									level="tertiary"
								>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function UnverifyPaymentForm({
	instalment,
	onSubmit,
	onCancel,
}: Readonly<VerifyPaymentFormProps>) {
	const [initialValues] = useState(() => ({
		instalments: [instalment.id],
		confirmed: false,
		// set the confirmation for batch to true incase of only one instalment
		confirmed_batch_instalment_count:
			Number(instalment.batch?.count) > 1 ? false : true,
	}))
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				if (
					!values.confirmed_batch_instalment_count &&
					Number(instalment.batch?.count) > 1
				) {
					throw new Error(
						"Please select the checkbox to confirm that this is a verified transaction."
					)
				}
				await onSubmit(values.instalments, instalment.batch?.id)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="2">
						<Heading as="h4">Payment Details</Heading>
						<Text color="muted">
							Please review the instalment and transaction details.
						</Text>
						<Box
							paddingX="6"
							padding="4"
							borderWidth="1"
							rounded="md"
							bgColor="primary"
							borderColor="primary"
						>
							<InstalmentOverview instalment={instalment} />
						</Box>
					</Stack>
					{instalment.batch?.id && Number(instalment.batch?.count || 0) > 1 ? (
						<Stack gap={"2"} paddingY={"4"}>
							<Heading fontSize={"md"}>
								Trips and instalments involved in the payment
							</Heading>
							<BulkInstalmentList
								include={"batch_details"}
								batchId={instalment.batch.id}
							/>
						</Stack>
					) : null}
					<Divider sm />
					<Alert status="warning">
						This will only remove the{" "}
						<Badge success>
							<Icons.BadgeCheckSolid /> verified
						</Badge>{" "}
						status and <b>WILL NOT</b> update the transaction details.
					</Alert>
					{instalment.batch &&
					Number(instalment.batch.count || 0) > 1 &&
					instalment.batch.paid_money_sum ? (
						<Box padding="4">
							<SwitchInputField
								name="confirmed_batch_instalment_count"
								label={
									<>
										This will unverify all{" "}
										{pluralize("instalments", instalment.batch.count, true)} for
										a total amount of{" "}
										{instalment.batch?.paid_money_sum?.currency}{" "}
										<Money money={instalment.batch?.paid_money_sum} /> under
										this payment.
									</>
								}
							/>
						</Box>
					) : null}
					<Divider />
					<Stack gap="4">
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								<Icons.Reply /> I understand, Unverify it.
							</Button>
							{onCancel ? (
								<Button
									disabled={submitting}
									onClick={() => onCancel()}
									level="tertiary"
								>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function InstalmentOverview({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	const { currency, txn_currency, txn_amount_money, batch } = instalment

	const batchTxnSum = batch?.transaction_money_sum
	const shouldTnxAmountShow =
		currency !== txn_currency && (txn_amount_money || batchTxnSum)
	const moneyValue = batchTxnSum ?? txn_amount_money

	return (
		<Stack gap="6">
			<Inline flexWrap="wrap" gap={{ xs: "4", md: "6" }}>
				<Stack gap="1">
					<Box fontSize="sm" color="muted">
						Paid Amount ({instalment.currency})
					</Box>
					<Box fontWeight="semibold">
						{instalment.batch?.paid_money_sum ? (
							<Money
								fontWeight="semibold"
								money={instalment.batch.paid_money_sum}
							/>
						) : (
							<Money
								amount={instalment.amount}
								currency={instalment.currency}
							/>
						)}
					</Box>
				</Stack>
				{shouldTnxAmountShow ? (
					<Stack gap="1">
						<Text fontSize="sm" color="muted">
							Transaction Amount ({txn_currency})
						</Text>
						<Box>
							{moneyValue && <Money fontWeight="semibold" money={moneyValue} />}
						</Box>
					</Stack>
				) : null}
				{instalment.batch?.count ? (
					<Stack gap="1">
						<Text fontSize="sm" color="muted">
							No. of Instalments
						</Text>
						<Text fontWeight="semibold">{instalment.batch.count}</Text>
					</Stack>
				) : null}

				{instalment.paid_at ? (
					<Stack gap="1">
						<Box fontSize="sm" color="muted">
							Paid On
						</Box>
						<Box fontWeight="semibold">
							<Time value={utcTimestampToLocalDate(instalment.paid_at)} />
						</Box>
					</Stack>
				) : null}
				{instalment.paid_by ? (
					<Stack gap="1">
						<Box fontSize="sm" color="muted">
							Logged By
						</Box>
						<Box fontWeight="semibold">{instalment.paid_by.name}</Box>
					</Stack>
				) : null}
			</Inline>
			<Inline flexWrap="wrap" gap={{ xs: "4", md: "6" }}>
				{instalment.debit_accounts?.length ? (
					<Box>
						<Box fontSize="sm" paddingBottom="1">
							Debit {pluralize("Account", instalment.debit_accounts.length)}
						</Box>
						<Box fontWeight="semibold">
							{instalment.debit_accounts.map((a) => a.name).join(", ")}
						</Box>
					</Box>
				) : null}
				{instalment.credit_accounts?.length ? (
					<Box>
						<Box fontSize="sm" paddingBottom="1">
							Credit {pluralize("Account", instalment.credit_accounts.length)}
						</Box>
						<Box fontWeight="semibold">
							{instalment.credit_accounts.map((a) => a.name).join(", ")}
						</Box>
					</Box>
				) : null}
				<Stack gap="1">
					<Box fontSize="sm" color="muted">
						Reference Id
					</Box>
					{instalment.reference_id ? (
						<Box fontWeight="semibold">{instalment.reference_id}</Box>
					) : (
						<Box color="muted">Not Provided</Box>
					)}
				</Stack>
			</Inline>
			<Inline gap="4">
				{instalment.verified_by ? (
					<Stack gap="1">
						<Box fontSize="sm" color="muted">
							<Icons.BadgeCheckSolid /> Verified On
						</Box>
						<Box fontWeight="semibold">
							<Time timestamp={instalment.verified_at} />
						</Box>
					</Stack>
				) : null}
				{instalment.verified_by ? (
					<Stack gap="1">
						<Box fontSize="sm" color="muted">
							Verified By
						</Box>
						<Box fontWeight="semibold">{instalment.verified_by.name}</Box>
					</Stack>
				) : null}
			</Inline>
		</Stack>
	)
}

function PaymentInProcess({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	const hasUnverifiedTx =
		instalment.pg_invoice?.unverified_successful_transactions?.length
	return (
		<Box
			paddingX="4"
			paddingY="2"
			borderWidth="1"
			borderColor={instalment.is_overdue ? "danger" : "primary"}
			rounded="md"
			bgColor={instalment.is_overdue ? "danger" : "primary"}
		>
			<Inline justifyContent="between" alignItems="center">
				<Inline
					gap="1"
					color={instalment.is_overdue ? "danger" : "primary"}
					fontWeight="semibold"
					alignItems="center"
				>
					<Icons.Clock />
					<Text>Payment {instalment.is_overdue ? "Overdue" : "Due on"}</Text>
					<Time
						timestamp={instalment.due_at}
						localTimestamp={instalment.due_at_local}
					/>
				</Inline>
				<Inline gap="2">
					<Button
						disabled
						size="sm"
						level="primary"
						status="primary"
						title="Payment was successful but the transaction could not be verified."
					>
						<Icons.Clock /> Payment in process...
					</Button>
				</Inline>
			</Inline>
			{hasUnverifiedTx ? <UnverifiedPGAlert /> : null}
		</Box>
	)
}

const UnverifiedPGAlert = () => {
	return (
		<>
			<Divider sm />
			<Alert title="Unverified PG Transaction" status="error">
				<Text>
					The payment for this instalment has been made using{" "}
					<Icons.LightningBoltSolid /> PG but the transaction could not be
					verified. The system will periodically try to verify the transaction.
					Once verified, the instalment will be automatically processed.
				</Text>
			</Alert>
		</>
	)
}

const LogPaymentSection = ({
	instalment,
	onChange,
	mutate,
}: {
	instalment: IInstalment
	onChange: () => void
	mutate: () => void
}) => {
	const xhr = useXHR()
	const { xs } = useBreakpoints()
	const pgIntegratedAppsCount = usePaymentGatewayIntegratedAppsCount()
	const { pg_invoice } = instalment
	const currentInstalmentStatus = useGetInstalmentState(instalment)
	return (
		<Stack gap="2">
			<LogTransaction
				instalment={instalment}
				onChange={() => {
					mutate()
					onChange?.()
				}}
			>
				{({ log }) => (
					<Box
						paddingX="4"
						paddingY="2"
						borderWidth="1"
						borderColor={instalment.is_overdue ? "danger" : "primary"}
						rounded="md"
						bgColor={instalment.is_overdue ? "danger" : "primary"}
					>
						<Inline
							justifyContent="between"
							alignItems="center"
							flexWrap="wrap"
							gap="2"
						>
							<Text>
								<Icons.Clock /> Payment{" "}
								{instalment.is_overdue ? "Overdue" : "Due on"}{" "}
								<Time
									timestamp={instalment.due_at}
									localTimestamp={instalment.due_at_local}
								/>
							</Text>
						</Inline>
						<Inline gap="2" flexWrap="wrap">
							<Button
								size={xs ? "sm" : "base"}
								onClick={log}
								level={
									pgIntegratedAppsCount && pg_invoice ? "secondary" : "primary"
								}
							>
								<Icons.Plus />
								{currentInstalmentStatus === "paymentPending"
									? "Logging Payment"
									: "Log Payment"}
							</Button>
							<IsPGPresent currency={instalment.currency}>
								<>
									{pgIntegratedAppsCount &&
									pg_invoice &&
									!instalment.paid_at &&
									!instalment.pg_invoice?.unverified_successful_transactions
										?.length ? (
										<CopyToClipboard>
											{({ copyTextToClipboard }) => (
												<CopyToClipboardButton
													onClick={() => copyTextToClipboard(pg_invoice.link)}
													level="primary"
												>
													<Inline gap="1" alignItems="center">
														<Icons.ClipboardCopy />
														<Text>Copy Link</Text>
													</Inline>
												</CopyToClipboardButton>
											)}
										</CopyToClipboard>
									) : null}

									{pg_invoice ? (
										<Component initialState={false}>
											{({ state, setState }) => (
												<>
													<RegenratePGLinkWithDeleteDialog
														paymentInvoice={pg_invoice}
														onChange={onChange}
														onRegenerate={() => setState(true)}
													/>
													<Dialog
														open={state}
														title="Regenerate Payment Link"
														onClose={() => setState(false)}
													>
														<Form
															initialValues={{ id: instalment.id }}
															onSubmit={withServerErrors(async ({ id }) => {
																await XHR(xhr).generatePGInvoice(id)
																showSnackbar("Payment link regenerated")
																onChange()
																setState(false)
															})}
															subscription={{ submitting: true }}
														>
															{({ handleSubmit, submitting }) => (
																<form onSubmit={handleSubmit} noValidate>
																	<Dialog.Body>
																		<Stack gap="4">
																			<Alert status="warning">
																				The existing link will be disabled.
																			</Alert>
																			<Text>
																				Are you sure you want to regenerate
																				payment link for this Instalment?
																			</Text>
																			<SubmissionError />
																		</Stack>
																	</Dialog.Body>
																	<Dialog.Footer>
																		<Button type="submit" disabled={submitting}>
																			{submitting
																				? "Regenerating..."
																				: "Yes, Regenerate"}
																		</Button>
																		<Button
																			disabled={submitting}
																			onClick={() => setState(false)}
																		>
																			No. Go Back.
																		</Button>
																	</Dialog.Footer>
																</form>
															)}
														</Form>
													</Dialog>
												</>
											)}
										</Component>
									) : (
										<GenerateLink instalment={instalment} onChange={onChange} />
									)}
								</>
							</IsPGPresent>
						</Inline>
					</Box>
				)}
			</LogTransaction>
			<FailedAttemptsWarning instalment={instalment} />
		</Stack>
	)
}

function FailedAttemptsWarning({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	const count = instalment.pg_invoice?.failed_transactions_count
	if (!count) {
		return null
	}
	return (
		<Box
			bgColor="warning"
			borderColor="warning"
			borderWidth="1"
			rounded="md"
			paddingX="4"
			paddingY="1"
		>
			<Inline
				justifyContent="start"
				alignItems="center"
				gap="2"
				color="warning"
			>
				<Icons.CircleAlert />
				<Text color="warning">
					{count} unsuccessful payment {pluralize("attempt", count)} recorded
				</Text>
			</Inline>
		</Box>
	)
}

function PaymentPaidSection({
	instalment,
	onChange,
	mutate,
	readOnly,
	hideRevert = false,
}: Readonly<{
	instalment: IInstalment
	onChange: () => void
	mutate: () => void
	readOnly: boolean | undefined
	hideRevert?: boolean
}>) {
	const isPaid = instalment.paid_at
	const isVerified = instalment.verified_at && instalment.verified_by
	if (!isPaid) return null
	return (
		<Box>
			<PaidPaymentBanner
				instalment={instalment}
				onChange={onChange}
				mutate={mutate}
				hideRevert={hideRevert}
			/>
			<PaymentDetailsSection instalment={instalment} />
			{isVerified ? (
				<VerifiedPaymentBanner
					instalment={instalment}
					readOnly={readOnly}
					onChange={onChange}
					mutate={mutate}
				/>
			) : (
				<UnverifiedPaymentBanner
					instalment={instalment}
					readOnly={readOnly}
					onChange={onChange}
					mutate={mutate}
				/>
			)}
		</Box>
	)
}

function UnverifiedPaymentBanner({
	instalment,
	readOnly,
	onChange,
	mutate,
}: Readonly<{
	instalment: IInstalment
	readOnly: boolean | undefined
	onChange: () => void
	mutate: () => void
}>) {
	return (
		<Box borderColor="warning" borderWidth="1" roundedBottom="md">
			<Box rounded="md" bgColor="warning" paddingX="4" paddingY="2">
				<Inline justifyContent="between">
					<Inline
						gap="1"
						alignItems="center"
						fontWeight="semibold"
						color="warning"
						fontSize="base"
					>
						<Icons.Security size="4" />
						<Text>Payment has not been verified yet</Text>
					</Inline>
					{!readOnly && instalment.can_verify_payment ? (
						<VerifyPaymentInDialog
							instalment={instalment}
							onSuccess={() => {
								mutate()
								onChange?.()
							}}
						>
							{({ verify }) => (
								<Button level="primary" onClick={() => verify()}>
									<Icons.BadgeCheckSolid /> Verify Payment
								</Button>
							)}
						</VerifyPaymentInDialog>
					) : null}
				</Inline>
			</Box>
		</Box>
	)
}

function VerifiedPaymentBanner({
	instalment,
	readOnly,
	onChange,
	mutate,
}: Readonly<{
	instalment: IInstalment
	readOnly: boolean | undefined
	onChange: () => void
	mutate: () => void
}>) {
	const { pg_invoice } = instalment
	return (
		<Box
			roundedBottom="md"
			bgColor="success"
			paddingX="4"
			paddingY="2"
			borderWidth="1"
			borderColor="success_emphasis"
		>
			<Inline justifyContent="between" alignItems="center" gap="2">
				<Inline
					gap="1"
					alignItems="center"
					fontWeight="semibold"
					color="success"
					fontSize="base"
				>
					<Text>
						<Icons.ShieldCheck size="4" /> Verified on{" "}
						<Time timestamp={instalment.verified_at} />
						{pg_invoice?.status === "paid" ? (
							<>
								{" "}
								via <Icons.LightningBolt /> PG
							</>
						) : instalment.verified_by?.name ? (
							<> by {instalment.verified_by.name}</>
						) : (
							<> by Unknown</>
						)}
					</Text>
				</Inline>
				<Box>
					{!readOnly && instalment.can_unverify_payment ? (
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<VerifyPaymentInDialog
									instalment={instalment}
									revert
									onSuccess={() => {
										mutate()
										onChange?.()
									}}
								>
									{({ verify }) => (
										<Button
											level="secondary"
											status="primary"
											onClick={() => verify()}
										>
											<Icons.Reply /> Unverify Payment
										</Button>
									)}
								</VerifyPaymentInDialog>
							</Dropdown.Menu>
						</Dropdown>
					) : null}
				</Box>
			</Inline>
		</Box>
	)
}

function PaymentSettlements({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	const pgInvoice = instalment.pg_invoice
	if (
		!pgInvoice?.successful_transactions?.filter(
			(t) => t.settlement_entries?.length
		).length
	) {
		return null
	}

	return (
		<Stack gap="2">
			<Divider sm borderColor="success" />
			<Stack gap="1" paddingX="4" paddingY="2">
				<Text fontSize="sm" color="muted">
					Settlements
				</Text>
				<Stack as="ul" listStyleType="disc" gap="2">
					{pgInvoice.successful_transactions.map((t) => (
						<Inline gap="1" as="li" key={t.id} flexWrap="wrap">
							<Money showCurrency keepTrailingZero money={pgInvoice.amount} />
							{t.fee_and_tax_bifurcation ? (
								<>
									<Inline gap="1">-</Inline>
									<Inline gap="1">
										<Money
											keepTrailingZero
											money={t.fee_and_tax_bifurcation.merchant_fee}
										/>
										<Text color="muted">(Fee)</Text>
									</Inline>
									<Inline gap="1">-</Inline>
									<Inline gap="1">
										<Money
											keepTrailingZero
											money={t.fee_and_tax_bifurcation.merchant_tax}
										/>
										<Text color="muted">(Tax)</Text>
									</Inline>
								</>
							) : null}
							<Inline gap="1">=</Inline>
							{t.setl_credit_amount && !isZeroMoney(t.setl_credit_amount) ? (
								<Inline gap="1">
									<Money
										showCurrency
										keepTrailingZero
										money={t.setl_credit_amount}
										fontWeight="semibold"
									/>
								</Inline>
							) : null}
							{t.setl_debit_amount && !isZeroMoney(t.setl_debit_amount) ? (
								<Inline gap="1">
									<Money
										showCurrency
										keepTrailingZero
										money={t.setl_debit_amount}
										color="danger"
									/>
								</Inline>
							) : null}
						</Inline>
					))}
				</Stack>
			</Stack>
		</Stack>
	)
}
function PaidPaymentBanner({
	instalment,
	onChange,
	mutate,
	hideRevert,
}: Readonly<{
	instalment: IInstalment
	onChange: () => void
	mutate: () => void
	hideRevert?: boolean
}>) {
	const { pg_invoice } = instalment
	const isGenrating = instalment.payment_receipt?.is_generating ?? false
	return (
		<Box
			paddingX="4"
			paddingY="2"
			borderWidth="1"
			borderColor="success_emphasis"
			roundedTop="md"
			bgColor="success"
		>
			<Inline
				justifyContent="between"
				alignItems="center"
				flexWrap="wrap"
				gap="2"
			>
				<Inline
					alignItems="center"
					gap="1"
					color="success"
					fontWeight="semibold"
				>
					<Text>
						{instalment.paid_at ? <Icons.ClockCheck /> : <Icons.Clock />} Paid
						on <Time fontWeight="semibold" timestamp={instalment.paid_at} />{" "}
						{pg_invoice?.status === "paid" ? (
							<>via {<Icons.LightningBolt />} PG</>
						) : instalment.paid_by ? (
							<>by {instalment.paid_by.name}</>
						) : null}
					</Text>
				</Inline>
				<Inline>
					{instalment.can_revert_payment && !hideRevert ? (
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<RevertPaymentDailog
									batchId={instalment.batch?.id || null}
									instalmentCount={instalment.batch?.count}
									bulkInstalmentAmount={instalment.batch?.paid_money_sum}
									onSuccess={() => {
										mutate()
										onChange?.()
									}}
									instalmentId={instalment.id}
								>
									{(params) => (
										<Button onClick={params.onRevert}>Revert Payment</Button>
									)}
								</RevertPaymentDailog>
							</Dropdown.Menu>
						</Dropdown>
					) : null}

					{instalment?.payment_receipt ? (
						<Inline alignItems="center" gap="2">
							<GeneratedReceiptDownload
								id={instalment.payment_receipt.id}
								fileUrl={instalment.payment_receipt.file_url}
								isGenerating={isGenrating}
								onChange={() => {
									mutate()
									onChange?.()
								}}
								size="sm"
							/>
							{isGenrating === false &&
							instalment.can_generate_receipt &&
							instalment.verified_at ? (
								<PaymentReceiptDialog
									instalmentId={instalment.id}
									onSuccess={() => {
										mutate()
										onChange?.()
									}}
								>
									{({ create }) => (
										<Button
											onClick={() => create()}
											title="Regenerate Receipt"
											inline
										>
											<Icons.Refresh opacity="50" />
										</Button>
									)}
								</PaymentReceiptDialog>
							) : null}
						</Inline>
					) : instalment.is_credit &&
					  instalment.verified_at !== null &&
					  instalment.verified_by !== null ? (
						<PaymentReceiptDialog
							instalmentId={instalment.id}
							onSuccess={() => {
								mutate()
								onChange?.()
							}}
						>
							{({ create }) => (
								<Button
									onClick={() => create()}
									inline
									size="sm"
									title="Generate Receipt"
								>
									<Button status="success" level="primary">
										<Icons.Receipt />
										Generate
									</Button>
								</Button>
							)}
						</PaymentReceiptDialog>
					) : null}
				</Inline>
			</Inline>
		</Box>
	)
}

function PaymentDetailsSection({
	instalment,
}: Readonly<{ instalment: IInstalment }>) {
	return (
		<Box
			borderWidth="1"
			borderColor="success_emphasis"
			borderTopColor="transparent"
			borderBottomColor="transparent"
			bgColor="success"
		>
			<Stack gap="2" paddingY="2">
				<Inline gap={{ xs: "4", md: "6" }} flexWrap="wrap">
					<Grid>
						{instalment.debit_accounts?.length ? (
							<Col>
								<Stack gap="1">
									<Text fontSize="sm" color="muted">
										{pluralize("Account", instalment.debit_accounts.length)}{" "}
										Received from
									</Text>
									<Box fontWeight="normal">
										{instalment.debit_accounts.map((a) => a.name).join(", ")}
									</Box>
								</Stack>
							</Col>
						) : null}
						{instalment.credit_accounts?.length ? (
							<Col>
								<Stack gap="1">
									<Text fontSize="sm" color="muted">
										{pluralize("Account", instalment.credit_accounts.length)}{" "}
										Credited to
									</Text>
									<Box fontWeight="normal">
										{instalment.credit_accounts.map((a) => a.name).join(", ")}
									</Box>
								</Stack>
							</Col>
						) : null}
					</Grid>
				</Inline>

				<Inline gap={{ xs: "4", md: "6" }} flexWrap="wrap">
					<Grid>
						{instalment.reference_id ? (
							<Col>
								<Stack gap="1">
									<Text fontSize="sm" color="muted">
										Reference ID
									</Text>
									<Inline alignItems="center" gap="2">
										<Text>{instalment.reference_id}</Text>
										<CopyToClipboard>
											{({ copyTextToClipboard }) => (
												<>
													<CopyToClipboardButton
														onClick={async () => {
															if (instalment.reference_id) {
																return copyTextToClipboard(
																	instalment.reference_id
																)
															}
															return false
														}}
														inline
													>
														<Icons.Duplicate />
													</CopyToClipboardButton>
												</>
											)}
										</CopyToClipboard>
									</Inline>
								</Stack>
							</Col>
						) : null}
						<Col>
							<Stack gap="1">
								<Text fontSize="sm" color="muted">
									Transaction Amount ({instalment.txn_amount_money?.currency})
								</Text>
								<Box fontWeight="semibold">
									{instalment.txn_amount_money ? (
										<Money money={instalment.txn_amount_money} />
									) : (
										<Box color="muted">Not Set</Box>
									)}
								</Box>
							</Stack>
						</Col>
					</Grid>
				</Inline>

				<PaymentSettlements instalment={instalment} />
			</Stack>
		</Box>
	)
}

function GenerateLink({
	instalment,
	onChange,
}: Readonly<{
	instalment: IInstalment<unknown>
	onChange: () => void
}>) {
	const xhr = useXHR()
	const { xs } = useBreakpoints()
	const { payment } = instalment
	const { is_credit: isCredit, is_refund: isRefund } = payment
	const showGenerateLink = isCredit && !isRefund
	if (!showGenerateLink) return null
	return (
		<Form
			initialValues={{ id: instalment.id }}
			onSubmit={withServerErrors(async ({ id }) => {
				await XHR(xhr).generatePGInvoice(id)
				onChange()
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => {
				return (
					<form onSubmit={handleSubmit} noValidate>
						<Stack gap="2">
							<Inline gap="2" flexWrap="wrap">
								<Box>
									<Button
										type="submit"
										disabled={submitting}
										size={xs ? "sm" : "base"}
										level="primary"
									>
										<Icons.BankNotes />{" "}
										{submitting ? "Generating..." : "Generate Link"}
									</Button>
								</Box>
								<br />
							</Inline>
							<SubmissionError />
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

type UseInfiniteScrollParams = {
	containerRef: React.RefObject<HTMLElement | null>
	hasNextPage: boolean
	isLoading: boolean
	onLoadMore: () => void
	threshold?: number
	throttleMs?: number
}

function useInfiniteScroll({
	containerRef,
	hasNextPage,
	isLoading,
	onLoadMore,
	threshold = 100,
	throttleMs = 300,
}: UseInfiniteScrollParams) {
	useEffect(() => {
		const container = containerRef.current
		if (!container) return
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null

		const onScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				if (!container) return
				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + threshold
				if (isBottom && hasNextPage && !isLoading) {
					onLoadMore()
				}
				throttleTimeout = null
			}, throttleMs)
		}
		container.addEventListener("scroll", onScroll)
		return () => {
			container.removeEventListener("scroll", onScroll)
			if (throttleTimeout) clearTimeout(throttleTimeout)
		}
	}, [containerRef, hasNextPage, isLoading, onLoadMore, threshold, throttleMs])
}

type CursorMeta = ICursorListResponse<unknown>["meta"]

interface InfiniteScrollRenderProps<TItem> {
	items: TItem[]
	isLoading: boolean
	containerRef: React.RefObject<HTMLDivElement | null>
	retry: () => void
	errorMessage: string | null
}

interface InfiniteScrollProps<TItem, TMeta> {
	fetcher: (params: {
		cursor?: string
		limit: number
	}) => Promise<{ data: TItem[]; meta: TMeta }>
	resetKey?: string
	limit?: number
	onFirstPageMeta?: (meta: TMeta & CursorMeta) => void
	children: (props: InfiniteScrollRenderProps<TItem>) => ReactNode
}
export function InfiniteScroll<TItem, TMeta>({
	fetcher,
	resetKey, //  resetKey={`${filter}-${sort}-${page}`}
	limit = 10,
	onFirstPageMeta,
	children,
}: InfiniteScrollProps<TItem, TMeta>) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [items, setItems] = useState<TItem[]>([])
	const [requestStatus, setRequestStatus] = useState<ApiRequestStatus>({
		isLoading: false,
		errorMessage: "",
	})

	const paginationRef = useRef<(TMeta & CursorMeta) | undefined>(undefined)
	const isFetchingRef = useRef(false)

	const loadMore = useCallback(async () => {
		if (isFetchingRef.current) return
		isFetchingRef.current = true
		setRequestStatus(() => ({ errorMessage: "", isLoading: true }))

		try {
			const res = await fetcher({
				limit,
				cursor:
					(paginationRef.current as { next_cursor?: string })?.next_cursor ||
					undefined,
			})

			paginationRef.current = res.meta as TMeta & CursorMeta

			setItems((prev) =>
				(res.meta as CursorMeta).prev_cursor ? [...prev, ...res.data] : res.data
			)

			if (!(res.meta as CursorMeta)?.prev_cursor) {
				onFirstPageMeta?.(res.meta as TMeta & CursorMeta)
			}
			setRequestStatus(() => ({ errorMessage: "", isLoading: false }))
		} catch (e) {
			const err = e instanceof Error ? e : new Error("Something went wrong")
			setRequestStatus(() => ({ errorMessage: err.message, isLoading: false }))
		} finally {
			isFetchingRef.current = false
		}
	}, [fetcher, limit, onFirstPageMeta])

	useInfiniteScroll({
		containerRef,
		hasNextPage: !!(paginationRef.current as CursorMeta)?.next_cursor,
		isLoading: requestStatus.isLoading || isFetchingRef.current,
		onLoadMore: loadMore,
	})

	const initialCall = useCallback(() => {
		paginationRef.current = undefined
		setItems([])
		loadMore()
	}, [loadMore, paginationRef])

	// reset when deps change , change filter or sort
	useEffect(() => {
		initialCall()
	}, [resetKey, initialCall])

	return (
		<>
			{children({
				items,
				isLoading: requestStatus.isLoading,
				containerRef,
				retry: initialCall,
				errorMessage: requestStatus.errorMessage,
			})}
		</>
	)
}

export const BulkInstalmentList = memo(function BulkInstalmentList({
	batchId,
	include,
}: {
	batchId: number
	include?: string
}) {
	const xhr = useXHR()
	const onFetch = useCallback(
		async function ({
			cursor,
			limit,
		}: {
			cursor?: string
			limit: number
		}): Promise<{ data: IInstalment[]; meta: CursorMeta }> {
			const res = await instalmentsXHR(xhr).getInstallmentsWithCursorPagination(
				{
					cursor,
					limit,
					batch_id: batchId,
					include,
				}
			)
			return {
				data: res.data,
				meta: res.meta,
			}
		},
		[include, batchId, xhr]
	)

	return (
		<InfiniteScroll<IInstalment, CursorMeta> limit={8} fetcher={onFetch}>
			{({ items, isLoading, retry, errorMessage, containerRef }) => (
				<Box rounded="lg" borderBottomColor={"default"} borderWidth={"1"}>
					{errorMessage ? (
						<RefreshComponent message={errorMessage} onRefresh={retry} />
					) : null}

					<Box
						ref={containerRef}
						style={{
							maxHeight: 300,
							overflowY: "auto",
						}}
					>
						<BulkPaymentInstalmentTable showContact instalments={items} />
						{isLoading ? (
							<SpinnerWithMessage message={"Loading date ..."} />
						) : null}
					</Box>
				</Box>
			)}
		</InfiniteScroll>
	)
})

function RevertPaymentDailog({
	children,
	batchId,
	instalmentCount,
	onSuccess,
	bulkInstalmentAmount,
	instalmentId,
}: {
	children: (props: { onRevert: () => void }) => React.ReactNode
	instalmentCount: number | undefined
	batchId: number | null
	onSuccess: () => void
	bulkInstalmentAmount?: Exclude<
		IInstalment["batch"],
		undefined
	>["paid_money_sum"]
	instalmentId: number
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({ onRevert: () => open() })}
			<Dialog open={isOpen} onClose={close} title={`Revert Payment`} md>
				<Form<{ comments: string }>
					onSubmit={withServerErrors(async (values) => {
						if (
							window.confirm("Are you sure you want to revert this payment?")
						) {
							const revertTransaction = batchId
								? XHR(xhr).revertBulkInstalmentTransactions
								: XHR(xhr).revertInstalmentTransactions

							await revertTransaction(batchId ?? instalmentId, {
								comments: values.comments,
							})
							showSnackbar("Reverted successfully.")
							onSuccess()
						}
					})}
					validate={validateFormValues(
						Validator.object().shape({
							comments: Validator.string().required(),
						})
					)}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => {
						return (
							<form noValidate onSubmit={handleSubmit}>
								<DialogBody>
									<Stack gap="4">
										{batchId ? (
											<>
												<Text fontSize={"base"}>
													This payment was created by combining{" "}
													<b>{instalmentCount}</b> instalments into a single
													payment totalling
													{bulkInstalmentAmount ? (
														<>
															{" "}
															<Money
																fontWeight={"semibold"}
																money={bulkInstalmentAmount}
															/>{" "}
														</>
													) : null}
													Reverting this action will undo the payment for all
													associated installments.Please proceed only if this
													payment was recorded incorrectly.
												</Text>

												<Box>
													<BulkInstalmentList batchId={batchId} />
												</Box>
											</>
										) : (
											<Text fontSize={"base"}>
												Reverting this action will undo the payment; proceed
												only if it was recorded incorrectly.
											</Text>
										)}

										<TextInputField
											name="comments"
											label="Comments"
											required
											autoFocus
											type="text"
										/>
									</Stack>
								</DialogBody>
								<DialogFooter>
									<Stack gap="4">
										<SubmissionError />
										<Inline gap={"4"}>
											<Button
												type="submit"
												disabled={submitting}
												level="primary"
												status="warning"
											>
												Revert
											</Button>
											<Button
												disabled={submitting}
												level="tertiary"
												onClick={close}
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</DialogFooter>
							</form>
						)
					}}
				</Form>
			</Dialog>
		</>
	)
}
